import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import cn from 'classnames';
import { ShowMoreToggle, ResetFilterButton } from '../index';
import { DiscoveryFilter } from '../../../../../store/features/discovery';
import styles from './FilterFacetGroup.module.scss';

export interface FilterFacetGroupProps {
  type: 'radio' | 'checkbox';
  name: string;
  maxFiltersToShowCount: number;
  renderFilterItem: (
    filter: DiscoveryFilter,
    type: FilterFacetGroupProps['type'],
  ) => React.ReactNode;
  heading: React.ReactNode;
  filters: DiscoveryFilter[];
}

export function FilterFacetGroup({
  type,
  name,
  maxFiltersToShowCount,
  heading,
  filters,
  renderFilterItem,
}: FilterFacetGroupProps) {
  const [listVisible, setListVisible] = useState<boolean>(true);
  const [listExpanded, setListExpanded] = useState<boolean>(false);
  const { setValue, watch } = useFormContext();
  const filterValues = watch(name);

  const resetFilterHandler = useCallback(() => {
    setValue(name, type === 'radio' ? ['all'] : []);
  }, [name, setValue, type]);

  const isFilterInactive = useCallback((values: string[] = []) => {
    if (values.length === 1) {
      return values.includes('all');
    }

    return values.length === 0;
  }, []);

  const showCount =
    filters.length - maxFiltersToShowCount === 1
      ? filters.length
      : maxFiltersToShowCount;
  const displayedFilters = listExpanded ? filters : filters.slice(0, showCount);
  const showMoreCount = filters.length - showCount;

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.header}
        onClick={() => {
          setListVisible(!listVisible);
        }}
      >
        <div className={styles.heading}>{heading}</div>

        <div className={styles.toolBar}>
          <ResetFilterButton
            className={styles.resetButton}
            onClick={(event) => {
              event.stopPropagation();
              resetFilterHandler();
            }}
            disabled={isFilterInactive(filterValues)}
          >
            Clear
          </ResetFilterButton>

          <span className={cn('material-icons', styles.collapseToggleIcon)}>
            {listVisible ? 'expand_less' : 'expand_more'}
          </span>
        </div>
      </div>
      {listVisible && (
        <ul className={styles.list}>
          {displayedFilters.map((filter) => (
            <li key={filter.label} className={styles.listItem}>
              {renderFilterItem(filter, type)}
            </li>
          ))}
        </ul>
      )}
      {listVisible && (
        <div className={styles.footer}>
          {showMoreCount > 0 && (
            <ShowMoreToggle
              count={showMoreCount}
              open={listExpanded}
              onClick={() => {
                setListExpanded(!listExpanded);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
